import React, { useState, useEffect, useMemo } from "react";
import { Button, DataTable, ActionButton } from "./ui";
import { useNavigate } from "react-router-dom";
import UploadFileModal from "./Modals/UploadFileModal";
import { MDBContainer, MDBTooltip } from "mdb-react-ui-kit";
import { api } from "../services";
import Toolbar from "./Toolbar";
import Actionbar from "./Actionbar";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CategoryModal from "./Modals/CategoryModal";
import {
  isPosReadOnlyEntity,
  isPosIntegrationDeleteLocked,
  posReadOnlyTooltip,
  posDeleteLockedTooltip,
} from "../utils/posReadOnly";
import { usePermission } from "../hooks/usePermission";
import { RBAC_MODULES, PERMISSION_ACTIONS } from "../config/routePermissions";
import { getLocalizedDisplayText } from "../utils/arabicTranslationUtils";
import {
  resolveDateLocale,
  formatLocalizedDateTime,
} from "../utils/formatDateTime";
import { LocalizedReadOnlyText } from "./MissingTranslationBadge";

/** Maps DataTable column ids to GET /categories sortBy fields */
const CATEGORY_TABLE_SORT_FIELDS = {
  nameEn: "name.en",
  nameAr: "name.ar",
  store: "store",
  createdAt: "createdAt",
};

function CategoryOverview() {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const dateLocale = resolveDateLocale(i18n.language);
  const { can } = usePermission();
  const canAddCategory = can(RBAC_MODULES.CATEGORIES, PERMISSION_ACTIONS.ADD);
  const canImportCategories =
    can(RBAC_MODULES.CATEGORIES, "import") ||
    can(RBAC_MODULES.CATEGORIES, PERMISSION_ACTIONS.ADD);
  const canEditCategory = can(RBAC_MODULES.CATEGORIES, PERMISSION_ACTIONS.EDIT);
  const canDeleteCategory = can(RBAC_MODULES.CATEGORIES, PERMISSION_ACTIONS.DELETE);

  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [stores, setStores] = useState([]);
  const [storeMap, setStoreMap] = useState({});
  const [selectedStoreFilter, setSelectedStoreFilter] = useState("");
  const [selectedStoreOption, setSelectedStoreOption] = useState(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categoryModalLoading, setCategoryModalLoading] = useState(false);
  const [tableSortColumn, setTableSortColumn] = useState("createdAt");
  const [categorySortField, setCategorySortField] = useState("createdAt");
  const [categorySortDirection, setCategorySortDirection] = useState("desc");

  const navigate = useNavigate();

  useEffect(() => {
    fetchAllCategories();
  }, []);

  useEffect(() => {
    // Fetch user's stores to map storeId -> store name for display
    const fetchStores = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await api.get(`/stores/my`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const storesArr = response.data?.stores || [];
        setStores(storesArr);
        const map = storesArr.reduce((acc, s) => {
          acc[s._id] = s.name;
          return acc;
        }, {});
        setStoreMap(map);
      } catch (err) {
        console.error("Error fetching stores for categories:", err.message);
      }
    };

    fetchStores();
  }, []);

  useEffect(() => {
    const q = (searchQuery || "").trim();
    const h = setTimeout(() => {
      setDebouncedSearchQuery(q);
    }, 700);
    return () => clearTimeout(h);
  }, [searchQuery]);

  useEffect(() => {
    fetchAllCategories(1, selectedStoreFilter, debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  
  

  const fetchAllCategories = async (
    pageArg = page,
    storeIdArg = selectedStoreFilter,
    searchArg = "",
    sortBy = categorySortField,
    sortOrder = categorySortDirection,
    limitArg = limit
  ) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      params.set("page", String(pageArg));
      params.set("limit", String(limitArg));
      params.set("sortBy", sortBy);
      params.set("sortOrder", sortOrder);
      
      // Optional store filter from UI only (backend scopes by role; no session storeId on this route)
      if (storeIdArg) {
        params.set("storeId", storeIdArg);
      }
      
      if ((searchArg || "").trim()) params.set("search", (searchArg || "").trim());

      const response = await api.get(`/categories?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const cats = Array.isArray(response.data?.categories)
        ? response.data.categories
        : Array.isArray(response.data?.data?.categories)
        ? response.data.data.categories
        : [];

      setCategories(cats);
      setFilteredCategories(cats);

      const totalItems =
        response.data?.totalItems ??
        response.data?.total ??
        response.data?.data?.total ??
        cats.length;

      const totalPagesResp =
        response.data?.totalPages ??
        response.data?.data?.totalPages ??
        Math.max(1, Math.ceil((totalItems || cats.length) / limit));

      setTotal(totalItems || cats.length);
      setTotalPages(totalPagesResp);
      setPage(pageArg);
    } catch (err) {
      console.error("Error fetching categories:", err.message);
    } finally {
      setLoading(false);
    }
  };

  

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleStoreFilterSelect = (store) => {
    const id = store?._id || "";
    setSelectedStoreFilter(id);
    setSelectedStoreOption(store || null);
    setPage(1);
    fetchAllCategories(1, id, searchQuery);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchAllCategories(newPage, selectedStoreFilter, debouncedSearchQuery);
    }
  };

  const applyTableSort = (column, direction) => {
    const apiField = CATEGORY_TABLE_SORT_FIELDS[column];
    if (!apiField) return;

    setTableSortColumn(column);
    setCategorySortField(apiField);
    setCategorySortDirection(direction);
    setPage(1);
    fetchAllCategories(1, selectedStoreFilter, debouncedSearchQuery, apiField, direction);
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setShowDeleteConfirm(true);
  };

  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem("token");
      await api.delete(`/categories/${categoryToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t("Category deleted successfully."));
      setShowDeleteConfirm(false);
      setCategoryToDelete(null);
      fetchAllCategories(page, selectedStoreFilter, searchQuery);
    } catch (err) {
      toast.error(
        err?.response?.data?.message
          ? t(err.response.data.message, {
              defaultValue: err.response.data.message,
            })
          : t("Failed to delete category.")
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setCategoryToDelete(null);
  };

  const handleEditCategory = (category) => {
    const storeIdForCategory = category?.store || category?.storeId;
    const storeName = storeMap[storeIdForCategory] || "N/A";
    setSelectedCategory({ ...category, storeName });
    setShowCategoryModal(true);
  };

  const handleCategoryModalClose = () => {
    setShowCategoryModal(false);
    setSelectedCategory(null);
  };

  const handleCategoryUpdateSuccess = () => {
    fetchAllCategories(page, selectedStoreFilter, searchQuery);
    setShowCategoryModal(false);
    setSelectedCategory(null);
  };

  const columns = useMemo(
    () => [
      {
        id: "nameEn",
        header: `${t("name")} (EN)`,
        sortable: true,
        render: (category) => category.name?.en,
      },
      {
        id: "nameAr",
        header: `${t("name")} (AR)`,
        sortable: true,
        render: (category) => (
          <LocalizedReadOnlyText
            {...getLocalizedDisplayText(
              category.name?.en,
              category.name?.ar,
              isArabic,
              "title"
            )}
          />
        ),
      },
      {
        id: "store",
        header: t("store"),
        sortable: true,
        render: (category) => storeMap[category?.store || category?.storeId] || "N/A",
      },
      {
        id: "createdAt",
        header: t("Created At"),
        sortable: true,
        render: (category) =>
          formatLocalizedDateTime(category.createdAt, dateLocale),
      },
      {
        id: "actions",
        header: t("actions"),
        render: (category) => {
          const posBlockedDel = isPosIntegrationDeleteLocked(category);
          const noPermDel = !canDeleteCategory;
          const deleteDisabled = posBlockedDel || noPermDel || deleteLoading;
          const deleteTooltip = posBlockedDel
            ? posDeleteLockedTooltip(t, category)
            : noPermDel
              ? t("noPermissionToAccessThis")
              : undefined;
          const posBlockedEdit = isPosReadOnlyEntity(category);
          const noPermEdit = !canEditCategory;
          const editDisabled = posBlockedEdit || noPermEdit || deleteLoading;
          const editTooltip = posBlockedEdit
            ? posReadOnlyTooltip(t, category)
            : noPermEdit
              ? t("noPermissionToAccessThis")
              : undefined;

          return (
            <div
              className="d-flex gap-2 oh-table-actions"
              onClick={(e) => e.stopPropagation()}
            >
              <ActionButton
                variant="delete"
                disabled={deleteDisabled}
                title={deleteTooltip}
                onClick={() => handleDeleteClick(category)}
              />
              <ActionButton
                variant="edit"
                disabled={editDisabled}
                title={editTooltip}
                onClick={() => handleEditCategory(category)}
              />
            </div>
          );
        },
      },
    ],
    [t, isArabic, dateLocale, storeMap, canDeleteCategory, canEditCategory, deleteLoading]
  );

  return (
    <div className="category-overview-page-root oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <h1 className="page-title-header">{t("categoryOverview")}</h1>

        <div className="oh-page-section">
          <Actionbar
            title={t("quickAction")}
            subtitle={t("manageYouWorkSpace")}
            secondBtnClass="secondary"
            secondBtnText={t("importFromFile")}
            secondBtnOnClick={() => setShowModal(true)}
            secondBtnIcon="fileUpload"
            disabledBtn2={!canImportCategories}
            secondBtnTitle={
              !canImportCategories ? t("noPermissionToAccessThis") : undefined
            }
          />
        </div>
        <div className="oh-page-section">
          <Toolbar
            hideIcons={true}
            showSearch={true}
            searchPlaceHolder={t("searchCategories")}
            searchQuery={searchQuery}
            handleSearch={handleSearch}
            showDropDown2={true}
            options2={stores}
            handleSelect2={handleStoreFilterSelect}
            selectedOption2={selectedStoreOption}
            placeholder2={t("Select store")}
            label2={t("store")}
          />
        </div>
        <UploadFileModal
          show={showModal}
          handleClose={() => setShowModal(false)}
          type={"category"}
        />

        <DataTable
          tableId="category-overview"
          title={t("sidebar.categories")}
          columns={columns}
          data={filteredCategories}
          rowKey="_id"
          loading={loading}
          emptyState={t("No categories to display")}
          primaryAction={
            canAddCategory
              ? {
                  label: t("addCategory"),
                  onClick: () => navigate("/add-category"),
                }
              : {
                  label: t("addCategory"),
                  onClick: () => navigate("/add-category"),
                  disabled: true,
                  title: t("noPermissionToAccessThis"),
                }
          }
          sort={{
            column: tableSortColumn,
            direction: categorySortDirection,
            onChange: ({ column, direction }) =>
              applyTableSort(column, direction),
          }}
          pagination={{
            page,
            pageSize: limit,
            total,
            onPageChange: handlePageChange,
            onPageSizeChange: (size) => {
              setLimit(size);
              setPage(1);
              fetchAllCategories(1, selectedStoreFilter, debouncedSearchQuery, categorySortField, categorySortDirection, size);
            },
            pageSizeOptions: [10, 20, 50],
            showLabel: t("Show", { defaultValue: "Show" }),
            ofLabel: t("of", { defaultValue: "Of" }),
            resultsLabel: t("results", { defaultValue: "results" }),
            isRtl: isArabic,
            previousLabel: t("previous"),
            nextLabel: t("next"),
          }}
          isRtl={isArabic}
        />

        {/* Category Modal */}
        <CategoryModal 
          modalShow={showCategoryModal}
          category={selectedCategory}
          loading={categoryModalLoading}
          onClose={handleCategoryModalClose}
          onUpdateSuccess={handleCategoryUpdateSuccess}
        />

        {showDeleteConfirm && (
          <div
            className="modal show fade d-block"
            tabIndex="-1"
            role="dialog"
            style={{ background: "rgba(0,0,0,0.3)" }}
          >
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t("Confirm Delete")}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={handleCancelDelete}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>
                    {t("Are you sure you want to delete category")} {" "}
                    <strong>
                      {isArabic
                        ? categoryToDelete?.name?.ar
                        : categoryToDelete?.name?.en}
                    </strong>
                    ?
                  </p>
                </div>
                <div className="modal-footer">
                  <Button
                    color="secondary"
                    onClick={handleCancelDelete}
                    disabled={deleteLoading}
                  >
                    {t("cancel")}
                  </Button>
                  <Button
                    color="danger"
                    onClick={handleDeleteCategory}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {t("deleting")}...
                      </>
                    ) : (
                      `${t("delete")}`
                    )}
                  </Button>
                  
                </div>
              </div>
            </div>
          </div>
        )}
      </MDBContainer>
    </div>
  );
}

export default CategoryOverview;
